import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import { ENV_META } from '../lib/environments.js';
import { dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  Tile,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { Check, RunModes, Steps, activeEnv } from '../components/2c-parts.js';
import type { StepState } from '../components/2c-parts.js';
import '../screens-2c.css';

interface Config {
  baseUrl: string | null;
  hasKey: boolean;
  hasBasic: boolean;
  availabilityFeedEnabled: boolean;
  keySource: 'env' | 'none';
  pageLimit: number;
  throttleMs: number;
  lastCheckAt: string | null;
  lastCheckOk: boolean | null;
  lastCheckError: string | null;
}
interface TypeCounts {
  pages: number;
  pulled: number;
  valid: number;
  invalid: number;
  duplicates: number;
  applied: number;
  stale: number;
  failed: number;
  skipped: Record<string, number>;
}
interface Run {
  id: string;
  mode: 'dry_run' | 'apply';
  refresh: boolean;
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  types: string[];
  typeIndex: number;
  cursor: { after?: number | null };
  counts: Record<string, TypeCounts>;
  expected: Record<string, number>;
  errors: Array<{ type: string; eventId: string | null; error: string }>;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  reconcile: Array<{
    type: string;
    expected: number | null;
    pulled: number;
    applied: number;
    failed: number;
    diffPct: number | null;
    ok: boolean | null;
  }>;
}

const TYPE_LABEL: Record<string, string> = {
  contacts: 'Zákazníci',
  consents: 'Marketingové souhlasy',
  races: 'Závody',
  orders: 'Objednávky',
  support: 'Zprávy podpory',
  messages: 'Odeslané e-maily e-shopu',
  watchdogs: 'Hlídací psi',
  gifts: 'Dárkové poukazy',
};
const STATUS: Record<Run['status'], { label: string; tone: Tone }> = {
  queued: { label: 'čeká na spuštění', tone: 'neu' },
  running: { label: 'běží', tone: 'acc' },
  done: { label: 'hotovo', tone: 'ok' },
  failed: { label: 'zastaveno chybou', tone: 'bad' },
  cancelled: { label: 'zrušeno', tone: 'warn' },
};
const modeText = (r: Run): string =>
  `${r.mode === 'apply' ? 'ostrý zápis' : 'nanečisto (zkouška bez zápisu)'}${r.refresh ? ', obnovení' : ''}`;

export function EshopImport({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const canWrite = rights.write('settings');
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  const config = useQuery<Config>({
    queryKey: ['eshop-config'],
    queryFn: () => api.get('/api/eshop-import/config'),
  });
  const runs = useQuery<{ items: Run[] }>({
    queryKey: ['eshop-runs'],
    queryFn: () => api.get('/api/eshop-import/runs'),
    refetchInterval: (q) =>
      q.state.data?.items.some((r) => r.status === 'queued' || r.status === 'running')
        ? 3000
        : false,
  });
  const detail = useQuery<Run>({
    queryKey: ['eshop-run', selected],
    queryFn: () => api.get(`/api/eshop-import/runs/${selected}`),
    enabled: Boolean(selected),
    refetchInterval: 3000,
  });

  const refreshAll = (): Promise<void> => client.invalidateQueries({ queryKey: ['eshop-runs'] });
  const onError = (e: unknown): void => setError(e instanceof Error ? e.message : String(e));

  const save = useMutation({
    mutationFn: () => api.put('/api/eshop-import/config', baseUrl !== null ? { baseUrl } : {}),
    onSuccess: async () => {
      setError(null);
      await client.invalidateQueries({ queryKey: ['eshop-config'] });
    },
    onError,
  });
  const feed = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put('/api/eshop-import/config', { availabilityFeedEnabled: enabled }),
    onSuccess: async () => {
      setError(null);
      await client.invalidateQueries({ queryKey: ['eshop-config'] });
    },
    onError,
  });
  const check = useMutation({
    mutationFn: () => api.post('/api/eshop-import/check'),
    onSuccess: () => client.invalidateQueries({ queryKey: ['eshop-config'] }),
    onError,
  });
  const start = useMutation({
    mutationFn: (v: { mode: 'dry_run' | 'apply'; word?: string }) =>
      api.post<{ id: string }>('/api/eshop-import/runs', {
        mode: v.mode,
        refresh,
        confirmation: v.word,
      }),
    onSuccess: async (r) => {
      setError(null);
      setAsking(false);
      setSelected(r.id);
      await refreshAll();
    },
    onError: (e: unknown) => {
      setAsking(false);
      onError(e);
    },
  });
  const act = useMutation({
    mutationFn: (v: { id: string; what: 'cancel' | 'resume' }) =>
      api.post(`/api/eshop-import/runs/${v.id}/${v.what}`),
    onSuccess: refreshAll,
    onError,
  });
  const expected = useMutation({
    mutationFn: (v: { id: string; values: Record<string, number> }) =>
      api.put(`/api/eshop-import/runs/${v.id}/expected`, v.values),
    onSuccess: () => client.invalidateQueries({ queryKey: ['eshop-run', selected] }),
    onError,
  });

  if (config.isLoading) return <LoadingState what="napojení na e-shop" />;
  if (config.isError || !config.data)
    return (
      <ErrorState
        error={config.error}
        title="Napojení na e-shop se nepodařilo načíst"
        onRetry={() => void config.refetch()}
      />
    );
  const c = config.data;
  const items = runs.data?.items ?? [];
  const active = items.find((r) => r.status === 'queued' || r.status === 'running');
  const selectedRun = selected ? detail.data : undefined;
  const lastDry = items.find((r) => r.mode === 'dry_run' && r.status === 'done');
  const lastApply = items.find((r) => r.mode === 'apply' && r.status === 'done');
  const countsOk =
    Boolean(lastDry) &&
    (lastDry?.reconcile?.length ?? 0) > 0 &&
    lastDry?.reconcile.every((r) => r.ok === true) === true;
  const step = (done: boolean, now: boolean): StepState => (done ? 'done' : now ? 'now' : 'todo');
  const envName = ENV_META[activeEnv(me)].label;
  const connected = c.hasKey && Boolean(c.baseUrl);
  const startBlocked = !canWrite || Boolean(active) || start.isPending;
  const blockReason = !canWrite
    ? 'Import smí spustit jen správce.'
    : active
      ? 'Jiný import už běží. Počkejte na jeho dokončení nebo ho zrušte.'
      : !connected
        ? 'Nejdřív nastavte adresu e-shopu a klíč na serveru.'
        : undefined;

  return (
    <>
      <PageHeader
        level={2}
        title="Import z e-shopu"
        description="Stáhne historii zákazníků, závodů, objednávek a dalších záznamů z e-shopu do hubu. Z e-shopu se jen čte a žádná zpráva se neodešle."
      />
      <Steps
        label="Postup importu z e-shopu"
        steps={[
          {
            title: 'Zkouška bez zápisu',
            text: 'Hub záznamy stáhne a zkontroluje. Do databáze nic nezapíše.',
            state: step(Boolean(lastDry), true),
          },
          {
            title: 'Kontrola počtů',
            text: 'Porovnejte stažené počty s čísly, která znáte z e-shopu.',
            state: step(countsOk, Boolean(lastDry)),
          },
          {
            title: 'Ostrý běh',
            text: 'Až počty sedí, zapište historii do hubu.',
            state: step(Boolean(lastApply), countsOk),
          },
        ]}
      />
      <RunModes
        dry="Hub záznamy z e-shopu stáhne, zkontroluje je a porovná s kontrolními čísly. Do databáze nic nezapíše, takže ji můžete pustit kdykoli a opakovaně."
        live={`Totéž a navíc zapíše historii do databáze prostředí ${envName}. Nic se neodešle a zprávy z historie se nevytvářejí.`}
      />
      {error ? <ErrorState error={new Error(error)} title="Akce se nepovedla" /> : null}

      <Panel
        title="Napojení na e-shop"
        subtitle="Hub si historii stahuje z rozhraní e-shopu. Sdílený klíč se zadává jen na serveru (ESHOP_HISTORY_KEY), nikdy v administraci."
        action={
          connected ? (
            <StatusBadge tone="ok" icon="check">
              připraveno
            </StatusBadge>
          ) : (
            <StatusBadge tone="warn" icon="alert">
              chybí adresa nebo klíč
            </StatusBadge>
          )
        }
      >
        <div className="s2c-form">
          <Field label="Adresa e-shopu" hint="Odkud hub historii stahuje.">
            <input
              type="url"
              placeholder="https://e-shop.example.invalid"
              value={baseUrl ?? c.baseUrl ?? ''}
              onChange={(e) => setBaseUrl(e.target.value)}
              disabled={!canWrite}
            />
          </Field>
          <div>
            <p className="field-label">Sdílený klíč</p>
            {c.hasKey ? (
              <StatusBadge tone="ok" icon="check">
                nastaven na serveru
              </StatusBadge>
            ) : (
              <StatusBadge tone="warn" icon="alert">
                chybí na serveru (ESHOP_HISTORY_KEY)
              </StatusBadge>
            )}
            <p className="field-hint">Klíč se po zadání na serveru v administraci nezobrazuje.</p>
          </div>
          <div>
            <p className="field-label">HTTP přihlášení</p>
            {c.hasBasic ? (
              <StatusBadge tone="ok" icon="check">
                nastaveno
              </StatusBadge>
            ) : (
              <StatusBadge tone="neu" icon="alert">
                nenastaveno
              </StatusBadge>
            )}
            <p className="field-hint">
              Jen pro zkušební prostředí e-shopu za HTTP přihlášením (ESHOP_HISTORY_BASIC_USER a
              ESHOP_HISTORY_BASIC_PASSWORD v prostředí serveru). Hodnoty se nezobrazují.
            </p>
          </div>
        </div>
        <div className="s2c-actions">
          <button
            type="button"
            className="btn pri"
            disabled={!canWrite || save.isPending}
            onClick={() => save.mutate()}
          >
            Uložit adresu
          </button>
          <button
            type="button"
            className="btn"
            disabled={!canWrite || check.isPending}
            onClick={() => check.mutate()}
          >
            {check.isPending ? 'Kontroluji…' : 'Zkontrolovat spojení'}
          </button>
        </div>
        <Check
          checked={c.availabilityFeedEnabled}
          disabled={!canWrite || feed.isPending}
          onChange={(v) => feed.mutate(v)}
          label="Hodinové stahování dostupnosti vstupenek"
          hint="Záchranná síť pro případ, že e-shop pošle událost o vstupence pozdě nebo vůbec. Jen čte z e-shopu; zapnuté se spouští každou hodinu."
        />
        {c.lastCheckAt ? (
          <p className="note" role="status">
            Poslední kontrola {dateTime(c.lastCheckAt)}:{' '}
            {c.lastCheckOk ? (
              <StatusBadge tone="ok" icon="check">
                spojení funguje
              </StatusBadge>
            ) : (
              <StatusBadge tone="bad" icon="alert">
                {c.lastCheckError ?? 'spojení selhalo'}
              </StatusBadge>
            )}
          </p>
        ) : null}
      </Panel>

      <Panel
        title="Nový běh"
        subtitle="Zákazníci, závody, objednávky, podpora, zprávy, hlídací psi, dárky; hub je stahuje v tomto doporučeném pořadí."
      >
        <Check
          checked={refresh}
          onChange={setRefresh}
          label="Obnovení"
          hint="Znovu stáhne záznamy změněné po předchozím importu. Bez obnovení se stahuje od začátku nebo od místa přerušení."
        />
        <div className="s2c-actions">
          <button
            type="button"
            className="btn pri"
            disabled={startBlocked || !connected}
            onClick={() => start.mutate({ mode: 'dry_run' })}
          >
            Zkouška bez zápisu
          </button>
          <button
            type="button"
            className="btn dng"
            disabled={startBlocked || !connected}
            onClick={() => setAsking(true)}
          >
            Importovat ostře
          </button>
          {blockReason ? <span className="s2c-reason">{blockReason}</span> : null}
        </div>
      </Panel>

      <Panel title="Běhy" subtitle="Nejnovější nahoře. Kliknutím na čas zobrazíte podrobnosti.">
        {runs.isLoading ? <LoadingState what="běhy" /> : null}
        {runs.isError ? (
          <ErrorState
            error={runs.error}
            title="Běhy se nepodařilo načíst"
            onRetry={() => void runs.refetch()}
          />
        ) : null}
        {runs.data && items.length === 0 ? (
          <EmptyState title="Zatím žádný běh" icon="upload">
            Začněte zkouškou bez zápisu. Nic se tím nezmění.
          </EmptyState>
        ) : null}
        {items.length > 0 ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Založen</th>
                  <th>Druh běhu</th>
                  <th>Stav</th>
                  <th>Co se stahuje</th>
                  <th>
                    <span className="sr-only">Akce</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className={`s2c-selrow${selected === r.id ? ' sel' : ''}`}>
                    <td>
                      <button
                        type="button"
                        className="linkbtn"
                        aria-pressed={selected === r.id}
                        onClick={() => setSelected(r.id)}
                      >
                        {dateTime(r.createdAt)}
                      </button>
                    </td>
                    <td>{modeText(r)}</td>
                    <td>
                      <StatusBadge tone={STATUS[r.status].tone}>
                        {STATUS[r.status].label}
                      </StatusBadge>
                    </td>
                    <td>{r.types.map((t) => TYPE_LABEL[t] ?? t).join(', ')}</td>
                    <td>
                      {r.status === 'queued' || r.status === 'running' ? (
                        <button
                          type="button"
                          className="btn"
                          disabled={!canWrite}
                          onClick={() => act.mutate({ id: r.id, what: 'cancel' })}
                        >
                          Zrušit běh
                        </button>
                      ) : null}
                      {r.status === 'failed' || r.status === 'cancelled' ? (
                        <button
                          type="button"
                          className="btn"
                          disabled={!canWrite || Boolean(active)}
                          onClick={() => act.mutate({ id: r.id, what: 'resume' })}
                        >
                          Pokračovat od místa přerušení
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>

      {selected && detail.isLoading ? <LoadingState what="podrobnosti běhu" /> : null}
      {selected && detail.isError ? (
        <ErrorState
          error={detail.error}
          title="Podrobnosti běhu se nepodařilo načíst"
          onRetry={() => void detail.refetch()}
        />
      ) : null}
      {selectedRun ? (
        <RunDetail
          run={selectedRun}
          canWrite={canWrite}
          onExpected={(values) => expected.mutate({ id: selectedRun.id, values })}
        />
      ) : null}

      <ConfirmDialog
        open={asking}
        title="Importovat historii z e-shopu ostře?"
        confirmLabel="Importovat ostře"
        danger
        requireWord="IMPORT"
        busy={start.isPending}
        onCancel={() => setAsking(false)}
        onConfirm={(word) => start.mutate({ mode: 'apply', word })}
      >
        <p>
          Historie z e-shopu se zapíše do databáze prostředí <b>{envName}</b>. Nic se neodešle a
          zprávy z historie se nevytvářejí. Doporučujeme předtím mít hotovou zkoušku bez zápisu se
          sedícími počty.
        </p>
      </ConfirmDialog>
    </>
  );
}

function RunDetail({
  run,
  canWrite,
  onExpected,
}: {
  run: Run;
  canWrite: boolean;
  onExpected: (v: Record<string, number>) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const totalPulled = Object.values(run.counts).reduce((a, x) => a + x.pulled, 0);
  const totalFailed = Object.values(run.counts).reduce((a, x) => a + x.failed + x.invalid, 0);
  const live = run.status === 'running' || run.status === 'queued';
  const dirty = Object.values(draft).some((v) => v !== '');
  const stateOf = (i: number): { text: string; tone: Tone } =>
    i < run.typeIndex || run.status === 'done'
      ? { text: 'hotovo', tone: 'ok' }
      : i === run.typeIndex && live
        ? { text: 'právě se stahuje', tone: 'acc' }
        : { text: 'čeká', tone: 'neu' };
  return (
    <>
      <Panel
        title={`Průběh: ${modeText(run)}`}
        subtitle={`Založeno ${dateTime(run.createdAt)}${run.finishedAt ? `, dokončeno ${dateTime(run.finishedAt)}` : ''}.`}
        action={
          <StatusBadge tone={STATUS[run.status].tone}>{STATUS[run.status].label}</StatusBadge>
        }
      >
        {live ? (
          <p className="s2c-runstate" role="status">
            Běh probíhá, stránka se sama obnovuje. Můžete ji nechat otevřenou nebo se vrátit
            později.
          </p>
        ) : null}
        {run.error ? <ErrorState error={new Error(run.error)} title="Běh skončil chybou" /> : null}
        <div className="tiles">
          <Tile
            label="Stav"
            value={STATUS[run.status].label}
            note={run.error ?? undefined}
            bad={run.status === 'failed'}
          />
          <Tile label="Staženo událostí" value={num(totalPulled)} />
          <Tile label="Chybných" value={num(totalFailed)} bad={totalFailed > 0} />
          <Tile
            label="Dokončeno druhů dat"
            value={`${Math.min(run.typeIndex, run.types.length)} / ${run.types.length}`}
          />
        </div>
        {run.types.map((t, i) => {
          const counts = run.counts[t];
          const st = stateOf(i);
          const total = run.expected[t];
          return (
            <div key={t} className="s2c-phase">
              <span>
                {TYPE_LABEL[t] ?? t}: {counts ? num(counts.pulled) : '0'}
                {total ? ` z ${num(total)}` : ''}{' '}
                <StatusBadge tone={st.tone}>{st.text}</StatusBadge>
              </span>
              {total ? (
                <progress
                  max={total}
                  value={Math.min(total, counts?.pulled ?? 0)}
                  aria-label={`Průběh: ${TYPE_LABEL[t] ?? t}`}
                />
              ) : null}
            </div>
          );
        })}
      </Panel>

      <Panel
        title="Srovnání s kontrolními čísly e-shopu"
        subtitle="Kontrolní čísla jsou počty záznamů, které jste zjistili přímo v e-shopu. Hub je porovná s tím, co stáhl; tolerance je 0,5 %, každá chybná událost je nesoulad."
      >
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Druh dat</th>
                <th>Kontrolní číslo</th>
                <th className="n">Staženo</th>
                <th className="n">Zapsáno</th>
                <th className="n">Chybných</th>
                <th className="n">Rozdíl</th>
                <th>Výsledek</th>
              </tr>
            </thead>
            <tbody>
              {run.types.map((t) => {
                const row = run.reconcile.find((r) => r.type === t);
                const counts = run.counts[t];
                return (
                  <tr key={t}>
                    <td>{TYPE_LABEL[t] ?? t}</td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        className="s2c-num"
                        aria-label={`Kontrolní číslo: ${TYPE_LABEL[t] ?? t}`}
                        disabled={!canWrite}
                        value={draft[t] ?? run.expected[t] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [t]: e.target.value })}
                      />
                    </td>
                    <td className="n">{counts ? num(counts.pulled) : '–'}</td>
                    <td className="n">{counts ? num(counts.applied) : '–'}</td>
                    <td className="n">{counts ? num(counts.failed + counts.invalid) : '–'}</td>
                    <td className="n">
                      {row?.diffPct === null || row?.diffPct === undefined
                        ? '–'
                        : `${row.diffPct.toFixed(2)} %`}
                    </td>
                    <td>
                      {row?.ok === true ? (
                        <StatusBadge tone="ok" icon="check">
                          sedí
                        </StatusBadge>
                      ) : row?.ok === false ? (
                        <StatusBadge tone="bad" icon="alert">
                          nesedí
                        </StatusBadge>
                      ) : (
                        <span className="muted">bez kontrolního čísla</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="s2c-actions">
          <button
            type="button"
            className="btn"
            disabled={!canWrite || !dirty}
            onClick={() =>
              onExpected(
                Object.fromEntries(
                  Object.entries(draft)
                    .filter(([, v]) => v !== '')
                    .map(([k, v]) => [k, Number(v)]),
                ),
              )
            }
          >
            Uložit kontrolní čísla
          </button>
        </div>
      </Panel>
      {Object.entries(run.counts).some(([, c]) => Object.keys(c.skipped).length > 0) ? (
        <Panel title="Přeskočené a chybné podle důvodu">
          <ul className="s2c-errlist">
            {Object.entries(run.counts).flatMap(([t, c]) =>
              Object.entries(c.skipped).map(([reason, n]) => (
                <li key={`${t}-${reason}`}>
                  {TYPE_LABEL[t] ?? t}: {reason} — {num(n)}
                </li>
              )),
            )}
          </ul>
        </Panel>
      ) : null}
      {run.errors.length > 0 ? (
        <Panel title="Chybné události" subtitle="Prvních 50; e-mailové adresy jsou maskované.">
          <ul className="s2c-errlist">
            {run.errors.map((e, i) => (
              <li key={i}>
                {TYPE_LABEL[e.type] ?? e.type} {e.eventId ?? ''}: {e.error}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </>
  );
}
